import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import ListSite from '../components/ListSite';
import ListDeclaration from '../components/ListDeclaration';
import ListDommage from '../components/ListDommage';
import ListContact from '../components/ListContact';
import { useEnums } from '../contexts/EnumContext';

import CustomSelect from '../utils/CustomSelect';
import useFetchIntervenants from '../hooks/useFetchIntervenants';

// même “modèle” que dans ListIntervenant
const FIELDS = [
    { name: 'Nom', label: 'Société', type: 'text', required: true },
    {
        name: '_Type',
        label: 'Type',
        type: 'select',
        required: true,
        options: [
            "Entreprise Générale",
            "Maître d'Oeuvre",
            "Maître d'Ouvrage",
            "BET",
            "Controleur Technique",
            "Fabricant",
            "Co-Traitant",
            "Sous-Traitant",
            "Autre",
        ],
    },
    { name: 'Activite', label: 'Activité', type: 'text', required: false },
    { name: 'Adresse', label: 'Adresse', type: 'text', required: true },
    { name: 'CP', label: 'Code Postal', type: 'text', required: false },
    { name: 'Ville', label: 'Ville', type: 'text', required: false },
    { name: 'Forme_juridique', label: 'Forme Juridique', type: 'text', required: false },
    { name: 'Siret', label: 'N° Siret', type: 'text', required: true },
    { name: 'Tel', label: 'Téléphone', type: 'text', required: false },
    { name: 'Mail', label: 'E-mail', type: 'email', required: true },
];

const TYPE_FIELD = FIELDS.find(f => f.name === '_Type');

const IntervenantDetails = () => {

    const { enums } = useEnums();
    const { uuid } = useParams();

    const {
        intervenant,
        fetchIntervenantByUUID,
        contacts,
        fetchContactsByIntervenantUUID,
        relIntervenants,
        fetchRelationsIntervenantsBySocieteUUID,
        fetchUpdateIntervenant
    }
        = useFetchIntervenants();

    const [activeTab, setActiveTab] = useState('coord');
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState(null);

    useEffect(() => {
        if (uuid) {
            fetchIntervenantByUUID(uuid);
            fetchContactsByIntervenantUUID(uuid);
            fetchRelationsIntervenantsBySocieteUUID(uuid);
        }
    }, [uuid]);

    useEffect(() => {
        if (intervenant) {
            setFormData({
                Nom: intervenant.Nom ?? '',
                _Type: intervenant._Type ?? '',
                Activite: intervenant.Activite ?? '',
                Adresse: intervenant.Adresse ?? intervenant.Adresse1 ?? '',
                CP: intervenant.CP ?? '',
                Ville: intervenant.Ville ?? '',
                Forme_juridique: intervenant.Forme_juridique ?? '',
                Siret: intervenant.Siret ?? '',
                Tel: intervenant.Tel ?? '',
                Mail: intervenant.Mail ?? '',
                Fax: intervenant.Fax ?? '',
                _Date_Crea: intervenant._Date_Crea ?? '',
                _Date_Modif: intervenant._Date_Modif ?? '',
            });
        }
    }, [intervenant]);

    if (!intervenant || !formData) {
        return (
            <div className="flex flex-1 justify-center items-center">
                <Loader />
            </div>
        );
    }

    const combi = relIntervenants.concat(contacts);

    const handleChange = (name, value) => {
        if (!isEditing) return;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleEditOrSave = async () => {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        const payload = {
            ...intervenant,
            ...formData,
        };
        try {
            await fetchUpdateIntervenant(payload);
            toast.success("Intervenant mis à jour avec succès");
        }
        catch (err) {
            console.error("Erreur lors de la mise à jour de l'intervenant :", err);
            if (err.status === 423) {
                toast.error(
                    "Impossible de modifier cet intervenant : il est verrouillé par un autre utilisateur."
                );
            } else {
                toast.error(
                    err.message || "Une erreur est survenue lors de l'enregistrement d'intervenant."
                );
            }
        }

        setIsEditing(false);
    };

    return (
        <div className="flex-1 px-4 lg:px-8">
            <div className="gap-6 grid grid-cols-12">
                <div className="gap-6 grid grid-cols-12 col-span-12 xxl:col-span-9">
                    <div className="col-span-12">
                        {/* HEADER */}
                        <div className="flex items-center bg-gray-900 drop-shadow-xl mb-6 px-8 rounded-lg w-full h-20">
                            <div
                                id="url"
                                className="flex flex items-center gap-x-2 font-semibold text-gray-400 text-sm sm:text-lg"
                            >
                                <Link
                                    to="/intervenants"
                                    className="hover:text-white decoration-orange-600 hover:underline hover:underline-offset-2 transition hover:translate-y-[-2px] duration-300"
                                >
                                    Intervenants
                                </Link>
                                <span className="cursor-default">/</span>
                                <Link
                                    to={`/intervenants/${intervenant.UUID_}`}
                                    className="hover:text-orange-600 transition hover:translate-y-[-2px] duration-300"
                                >
                                    {formData.Nom}
                                </Link>
                            </div>
                        </div>

                        <div className="mx-auto w-full">
                            {/* TABS */}
                            <div className="mb-4 border-gray-200 border-b">
                                <ul className="flex flex-wrap -mb-px" role="tablist">
                                    {[
                                        { id: 'coord', label: 'Coordonnées' },
                                        { id: 'sites', label: 'Sites' },
                                        { id: 'declas', label: 'Déclarations' },
                                        { id: 'doms', label: 'Dommages' },
                                        { id: 'contacts', label: 'Contacts' },
                                    ].map((tab) => (
                                        <li key={tab.id} className="mr-2" role="presentation">
                                            <button
                                                className={`text-base font-semibold text-center py-4 px-4 rounded-t-lg border-b-5 ${activeTab === tab.id
                                                    ? 'text-orange-600 borde-2 border-orange-300 '
                                                    : 'text-orange-400 border-transparent hover:text-orange-500'
                                                    } transition duration-300 ease-in-out`}
                                                onClick={() => setActiveTab(tab.id)}
                                                type="button"
                                                role="tab"
                                                aria-controls={tab.id}
                                                aria-selected={activeTab === tab.id}
                                            >
                                                {tab.label}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div id="myTabContent">
                                {/* ====== TAB COORDONNÉES ====== */}
                                {activeTab === 'coord' && (
                                    <div id="form-info" className="mx-auto">
                                        <div className="bg-white shadow-lg mb-6 p-4 md:p-8 px-4 rounded-lg">
                                            {/* Informations Générales */}
                                            <div
                                                id="general-info"
                                                className="gap-4 gap-y-2 grid grid-cols-2 md:grid-cols-10 text-sm"
                                            >
                                                <div className="md:flex justify-between items-center col-span-10 mb-4">
                                                    <h2 className="col-span-10 font-semibold text-lg">
                                                        Informations Générales
                                                    </h2>
                                                    <button
                                                        className="bg-orange-600 flex justify-center items-center p-4 rounded-lg mb-4 w-[130px] hover:bg-orange-500 transition duration-300 ease-in-out"
                                                        onClick={handleEditOrSave}
                                                    >
                                                        <span className="text-white items-center">
                                                            {isEditing ? 'Sauvegarder' : 'Modifier'}
                                                        </span>
                                                    </button>
                                                </div>

                                                <div className="md:flex gap-4 gap-y-2 col-span-10">
                                                    <div className="flex-1 mb-2">
                                                        <label>Nom</label>
                                                        <input
                                                            className={`${isEditing ? 'bg-white' : 'bg-orange-50'
                                                                } mt-1 px-4 border rounded-lg w-full h-10 font-bold cursor-text`}
                                                            value={formData.Nom}
                                                            disabled={!isEditing}
                                                            onChange={(e) => handleChange('Nom', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="flex-1 mb-2">
                                                        <label>Type</label>
                                                        <CustomSelect
                                                            isPortal
                                                            options={TYPE_FIELD.options ?? []}
                                                            value={formData._Type}
                                                            onChange={(val) =>
                                                                handleChange(
                                                                    "_Type",
                                                                    typeof val === "string" ? val : val?.label || ""
                                                                )
                                                            }
                                                            isDisabled={!isEditing}
                                                            className="mt-1 w-full"
                                                        />

                                                    </div>
                                                </div>

                                                <div className="md:flex gap-4 gap-y-2 col-span-10">
                                                    <div className="flex-1 mb-2">
                                                        <label>Activité</label>
                                                        <CustomSelect
                                                            isPortal
                                                            options={enums['Corp de Metier'] ?? []}
                                                            value={formData.Activite || 'Sélectionner'}
                                                            onChange={(val) =>
                                                                handleChange(
                                                                    "Activite",
                                                                    typeof val === "string" ? val : val?.label || ""
                                                                )
                                                            }
                                                            isDisabled={!isEditing}
                                                        />
                                                    </div>
                                                    <div className="flex-1 mb-2">
                                                        <label>Forme Juridique</label>
                                                        <CustomSelect
                                                            isPortal
                                                            options={enums[`Forme Juridique`] ?? []}
                                                            value={formData.Forme_juridique || 'Sélectionner'}
                                                            onChange={(val) =>
                                                                handleChange(
                                                                    "Forme_juridique",
                                                                    typeof val === "string" ? val : val?.label || ""
                                                                )
                                                            }
                                                            isDisabled={!isEditing}
                                                        />
                                                    </div>

                                                    <div className="flex-1 mb-2">
                                                        <label>N° Siret</label>
                                                        <input
                                                            className={`${isEditing ? 'bg-white' : 'bg-gray-50'
                                                                } mt-1 px-4 border rounded-lg w-full h-10 cursor-text`}
                                                            value={formData.Siret}
                                                            disabled={!isEditing}
                                                            onChange={(e) => handleChange('Siret', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Lieu */}
                                            <div
                                                id="location-info"
                                                className="gap-4 gap-y-2 grid grid-cols-2 md:grid-cols-10 mt-6 text-sm"
                                            >
                                                <h2 className="col-span-10 font-semibold text-lg">Lieu</h2>

                                                <div className="md:flex gap-4 gap-y-2 col-span-10">
                                                    <div className="flex-1 mb-2">
                                                        <label>Adresse</label>
                                                        <input
                                                            className={`${isEditing ? 'bg-white' : 'bg-gray-50'
                                                                } mt-1 px-4 border rounded-lg w-full h-10 cursor-text`}
                                                            value={formData.Adresse}
                                                            disabled={!isEditing}
                                                            onChange={(e) => handleChange('Adresse', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="flex-1 mb-2">
                                                        <label>Code Postal</label>
                                                        <input
                                                            className={`${isEditing ? 'bg-white' : 'bg-gray-50'
                                                                } mt-1 px-4 border rounded-lg w-full h-10 cursor-text`}
                                                            value={formData.CP}
                                                            disabled={!isEditing}
                                                            onChange={(e) => handleChange('CP', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="flex-1 mb-2">
                                                        <label>Ville</label>
                                                        <input
                                                            className={`${isEditing ? 'bg-white' : 'bg-gray-50'
                                                                } mt-1 px-4 border rounded-lg w-full h-10 cursor-text`}
                                                            value={formData.Ville}
                                                            disabled={!isEditing}
                                                            onChange={(e) => handleChange('Ville', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Contacts */}
                                            <div
                                                id="contact-info"
                                                className="gap-4 gap-y-2 grid grid-cols-2 md:grid-cols-10 mt-6 text-sm"
                                            >
                                                <h2 className="col-span-10 font-semibold text-lg">Contacts</h2>

                                                <div className="md:flex gap-4 gap-y-2 col-span-10">
                                                    <div className="flex-1 mb-2">
                                                        <label>Téléphone</label>
                                                        <input
                                                            className={`${isEditing ? 'bg-white' : 'bg-gray-50'
                                                                } mt-1 px-4 border rounded-lg w-full h-10 cursor-text`}
                                                            value={formData.Tel}
                                                            disabled={!isEditing}
                                                            onChange={(e) => handleChange('Tel', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="flex-1 mb-2">
                                                        <label>Fax</label>
                                                        <input
                                                            className={`${isEditing ? 'bg-white' : 'bg-gray-50'
                                                                } mt-1 px-4 border rounded-lg w-full h-10 cursor-text`}
                                                            value={formData.Fax}
                                                            disabled={!isEditing}
                                                            onChange={(e) => handleChange('Fax', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="flex-1 mb-2">
                                                        <label>Email</label>
                                                        <input
                                                            className={`${isEditing ? 'bg-white' : 'bg-gray-50'
                                                                } mt-1 px-4 border rounded-lg w-full h-10 cursor-text`}
                                                            value={formData.Mail}
                                                            disabled={!isEditing}
                                                            onChange={(e) => handleChange('Mail', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Historique de Création */}
                                            <div
                                                id="creation-history"
                                                className="gap-4 gap-y-2 grid grid-cols-2 md:grid-cols-10 mt-6 text-sm"
                                            >
                                                <h2 className="col-span-10 font-semibold text-lg">
                                                    Historique de Création
                                                </h2>

                                                <div className="md:flex gap-4 gap-y-2 col-span-10">
                                                    <div className="flex-1 mb-2">
                                                        <label>Date de Création</label>
                                                        <input
                                                            className="bg-gray-50 mt-1 px-4 border rounded-lg w-full h-10 cursor-text"
                                                            value={formData._Date_Crea}
                                                            disabled
                                                        />
                                                    </div>
                                                    <div className="flex-1 mb-2">
                                                        <label>Date de Modification</label>
                                                        <input
                                                            className="bg-gray-50 mt-1 px-4 border rounded-lg w-full h-10 cursor-text"
                                                            value={formData._Date_Modif}
                                                            disabled
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* AUTRES TABS */}
                                {activeTab === 'sites' && <ListSite lstSites={[]} />}
                                {activeTab === 'declas' && (
                                    <ListDeclaration lstDeclarations={[]} />
                                )}
                                {activeTab === 'doms' && <ListDommage lstDommages={[]} />}
                                {activeTab === 'contacts' && (
                                    <ListContact lstContacts={combi} intervenantUUID={uuid} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default IntervenantDetails;
